import { ref, computed, watch } from "vue";
import { I18n, ClientIDType, Countries, Client, connectionService } from "app-core";
import { useForm } from "./index53.js";
import { object, custom } from "./index101.js";
function req(value) {
  if (Array.isArray(value)) return !!value.length;
  if (value === void 0 || value === null) return false;
  if (value === false) return true;
  if (value instanceof Date) return !isNaN(value.getTime());
  if (typeof value === "object") {
    for (const _ in value) return true;
    return false;
  }
  return !!String(value).length;
}
const EMAIL_REGEX = /^(?:[A-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9]{2,}(?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;
function isValidEmail(value) {
  return !!value && EMAIL_REGEX.test(String(value).trim());
}
function createClientSchema() {
  return object({
    id: custom(),
    name: custom(),
    cost_center: custom(),
    address: custom(),
    postal_number: custom(),
    city: custom(),
    country: custom(),
    phone: custom(),
    email: custom(),
    tax_id_number: custom(),
    registration_number: custom(),
    vat_number: custom(),
    card: custom(),
    discount_percent: custom(),
    comment: custom(),
    id_type: custom(),
    id_number: custom(),
    tax_id_card_number: custom(),
    pricelist_id: custom()
  }).superRefine((data, ctx) => {
    const requiredIssue = (path) => ctx.addIssue({ code: "custom", message: I18n.t("validation.required"), path: [path] });
    const localCountry = I18n.c("general.country") === data.country;
    const localInfo = I18n.c("client.localRequiredInfo");
    const remoteInfo = I18n.c("client.remoteRequiredInfo");
    const requiredInfo = localCountry ? localInfo : remoteInfo;
    const nameRequired = localCountry ? localInfo.includes("name") || data.id_type !== ClientIDType.tax_id_number_local || !data.id_number : remoteInfo.includes("name");
    if (nameRequired && !req(data.name)) requiredIssue("name");
    if (req(data.email) && !EMAIL_REGEX.test(String(data.email))) {
      ctx.addIssue({ code: "custom", message: I18n.t("validation.email"), path: ["email"] });
    }
    for (const field of ["address", "city", "postal_number", "id_number"]) {
      if (requiredInfo.includes(field) && !req(data[field])) requiredIssue(field);
    }
    if (!req(data.country)) requiredIssue("country");
    if (!req(data.id_type)) requiredIssue("id_type");
    if (!req(data.discount_percent)) requiredIssue("discount_percent");
  });
}
function filterIdTypes(country) {
  return Client.idTypes().filter((idType) => {
    if (I18n.c("general.country") === "MNE" && country !== "MNE" && idType.id === ClientIDType.tax_id_number) return false;
    return true;
  });
}
function useClientForm(client, options) {
  const endpointBase = (options == null ? void 0 : options.endpointBase) ?? "";
  const schema = createClientSchema();
  function buildFormData(client2) {
    var _a;
    const country = (client2 == null ? void 0 : client2.country) || ((_a = Countries.data.find((c) => c.alpha3Code === I18n.c("general.country"))) == null ? void 0 : _a.alpha3Code) || "";
    const idTypesFiltered = filterIdTypes(country);
    return {
      id: (client2 == null ? void 0 : client2.id) || void 0,
      name: (client2 == null ? void 0 : client2.name) || "",
      cost_center: (client2 == null ? void 0 : client2.cost_center) || "",
      address: (client2 == null ? void 0 : client2.address) || "",
      postal_number: (client2 == null ? void 0 : client2.postal_number) || "",
      city: (client2 == null ? void 0 : client2.city) || "",
      country,
      phone: (client2 == null ? void 0 : client2.phone) || "",
      email: (client2 == null ? void 0 : client2.email) || "",
      tax_id_number: (client2 == null ? void 0 : client2.tax_id_number) || "",
      registration_number: (client2 == null ? void 0 : client2.registration_number) || "",
      vat_number: (client2 == null ? void 0 : client2.vat_number) || "",
      card: (client2 == null ? void 0 : client2.card) || "",
      discount_percent: client2 ? String(client2.getDiscountPercent()) : "0",
      comment: (client2 == null ? void 0 : client2.comment) || "",
      id_type: (client2 == null ? void 0 : client2.id_type) || idTypesFiltered[0].id,
      id_number: (client2 == null ? void 0 : client2.id_number) || "",
      tax_id_card_number: (client2 == null ? void 0 : client2.tax_id_card_number) || "",
      pricelist_id: (client2 == null ? void 0 : client2.pricelist_id) || void 0
    };
  }
  const form = useForm(schema, buildFormData(client));
  const detecting = ref(false);
  const idTypes = computed(() => filterIdTypes(form.data.country));
  watch(() => form.data.country, () => {
    form.data.id_type = idTypes.value[0].id;
    form.data.id_number = "";
  });
  watch(() => form.data.id_type, () => {
    form.validate();
  });
  function validateField(field) {
    const result = schema.safeParse(form.data);
    form.clearErrors(field);
    if (!result.success) {
      form.errors.value.push(...result.error.issues.filter((issue) => issue.path.join(".") === field).map((issue) => ({ name: field, message: issue.message })));
    }
  }
  async function detect() {
    detecting.value = true;
    const clientData = await Client.detect(form.data.id_number);
    detecting.value = false;
    if (!clientData) return;
    form.data.name = clientData.name || "";
    form.data.address = clientData.address || "";
    form.data.city = clientData.city || "";
    form.data.registration_number = clientData.registration_number || "";
    form.data.phone = clientData.phone_number || form.data.phone;
    form.data.email = clientData.email_address || form.data.email;
  }
  async function save() {
    var _a;
    if (form.validate().length) return { status: "invalid" };
    if (!form.data.name) {
      form.data.name = I18n.t("general.buyer");
      const localCountry = I18n.c("general.country") === form.data.country;
      if (!localCountry) form.data.name += " " + form.data.country;
      if ((_a = form.data.id_type) == null ? void 0 : _a.includes(ClientIDType.tax_id_number)) {
        form.data.name += ` (${I18n.t("clients.id_types.tax_id_number")}: ${form.data.id_number})`;
      }
    }
    if (!connectionService.isOnline) return { status: "offline" };
    const client2 = new Client(form.data);
    const taxIdNumber = client2.getTaxIdNumber();
    if (taxIdNumber) {
      form.data.tax_id_number = taxIdNumber;
    }
    try {
      const response = !form.data.id ? await form.post(`${endpointBase}/clients`) : await form.patch(`${endpointBase}/clients/${form.data.id}`);
      Client.insert({ data: [response.data] });
      return { status: "saved", client: new Client(response.data) };
    } catch {
      return { status: "error" };
    }
  }
  return { form, schema, idTypes, detecting, validateField, detect, save };
}
export {
  createClientSchema,
  isValidEmail,
  useClientForm
};
//# sourceMappingURL=index40.js.map
